/**
 * Legacy tag-URL compatibility.
 *
 * Before the Storefront-API filtering engine the collection page used Shopify's native
 * path-based tag filtering, and the client's pages still link to those URLs:
 *
 *   /collections/outerwear-jackets-vests/feature-packable+product_type-jackets
 *
 * Shopify still serves them — it tag-filters `collection.products` server-side, so the
 * first paint is correct — but the engine reads its selection from the query string
 * only. Without this module a legacy link renders an unfiltered sidebar over a filtered
 * grid, and the first filter click drops every filter the link carried.
 *
 * Two sources, in order:
 *
 *  1. `collection.current_tags`, exposed from Liquid as `CollectionConfig.legacyTags`.
 *     Those are the ORIGINAL tag strings (`feature=Packable`), so the mapping is exact.
 *  2. The handleized path segment (`feature-packable`), matched by handleizing every
 *     configured option and comparing. `| handle` is one-way and lossy — `t-shirts`
 *     can't be turned back into `T-Shirts` — so this only resolves values the sidebar
 *     already knows about. It's the fallback for when Liquid hands us nothing.
 *
 * A tag that maps to no group at all — `Electronics`, or the old `New` filter's
 * `September 2025` — is preserved as itself through LEGACY_TAG_GROUP_KEY, because native
 * filtering worked on any tag and dropping it would widen the link to the whole
 * collection. A tag that maps to a group but to a value that group's sidebar doesn't
 * offer (`FilterCustom`, or `fit=Slim` in a collection with no fit tags) is kept under
 * that group with its own value: the tag is still on the products, so filtering by it
 * reproduces exactly what the legacy URL showed. Where the sidebar does offer the value,
 * its spelling wins, which is what settles the `adidas` / `Adidas` vendor case split.
 *
 * The exceptions are Price and Size, the two groups whose underlying mechanism changed —
 * from tags to native ranges and variant options. Their legacy tags can't be mixed with
 * the new controls (a hidden `price=Under $25` tag filter alongside a visible `$50 to
 * $100` range would silently contradict it), so such a tag has to become offered buckets
 * or nothing. `Under $25` is exactly `Under $10` + `$10 to $25`, so it expands into both
 * (LEGACY_PRICE_EXPANSIONS); `Under $5` and standard S/M/L/XL sizes have no exact cover
 * and are dropped, landing on the unfiltered collection. That's honest — widening
 * `Under $5` to `Under $10` would show products the link never matched.
 */

import {
  PRICE_RANGES,
  SIZE_SYNONYMS,
  getGroup,
  resolveTag,
  tagForValue,
} from './FilterConfig';
import { FilterGroupConfig, FilterSelection } from './types';

/** Structural shape of `Merchology.CollectionFilterOptions` (see FilterSidebar). */
export interface LegacyOptionGroup {
  key: string;
  options: { value: string }[];
}

export interface LegacyAdoption {
  /** Filters the legacy URL resolved to, in the new engine's shape. */
  selection: FilterSelection;
  /** The collection URL with the tag segment removed. */
  canonicalPath: string;
  /** Legacy tags/tokens that mapped onto a filter. */
  matched: string[];
  /** Legacy tags/tokens with no equivalent in the new system. */
  dropped: string[];
  /** 'tags' when Liquid's current_tags drove it, 'path' when the fallback did. */
  source: 'tags' | 'path';
}

/**
 * Legacy tag prefixes whose group is no longer tag-backed. Everything else resolves
 * through `resolveTag`, which already knows every tag group's prefix (and the `Filter`
 * colour sentinel).
 */
const LEGACY_PREFIX_GROUPS: Record<string, string> = {
  'brand=': 'brand',
  'product_type=': 'product_type',
};

/**
 * Legacy price buckets the offered ones cover EXACTLY, and how. The client still hand-writes
 * links like `/collections/x/price-under-25`, which has no single equivalent — but it is
 * precisely `Under $10` + `$10 to $25`, so it expands into both and the shopper sees the same
 * products with two chips they can adjust.
 *
 * Only exact covers belong here. `Under $5`'s narrowest cover is `Under $10`, which would add
 * $5–$10 products the link never promised, so it stays dropped. `mergePriceRanges` collapses
 * an expansion back into one interval, so the extra bucket costs nothing at query time.
 */
export const LEGACY_PRICE_EXPANSIONS: Record<string, string[]> = {
  'Under $25': ['Under $10', '$10 to $25'],
};

const SIZE_INDEX: Record<string, string> = buildSizeIndex();

function buildSizeIndex(): Record<string, string> {
  const idx: Record<string, string> = {};
  Object.keys(SIZE_SYNONYMS).forEach((canonical) => {
    idx[canonical.toLowerCase()] = canonical;
    SIZE_SYNONYMS[canonical].forEach((spelling) => {
      const k = spelling.toLowerCase();
      if (!idx.hasOwnProperty(k)) idx[k] = canonical;
    });
  });
  return idx;
}

/**
 * Shopify's `| handle`: lowercase, strip diacritics, collapse every run of characters
 * that isn't a letter, digit or underscore into a single `-`, trim the ends. Underscores
 * survive, which is why `product_type=Jackets` becomes `product_type-jackets`.
 */
export function handleize(value: string): string {
  let out = (value || '').toLowerCase();
  if (typeof (String.prototype as any).normalize === 'function') {
    try {
      out = (out as any).normalize('NFD').replace(/[̀-ͯ]/g, '');
    } catch (e) {
      /* a platform without NFD just keeps the accented characters */
    }
  }
  return out.replace(/[^a-z0-9_]+/g, '-').replace(/^-+/, '').replace(/-+$/, '');
}

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch (e) {
    return value;
  }
}

/** `/collections/<handle>` for any path under it, preserving a locale prefix. */
function collectionBasePath(pathname: string): string | null {
  const match = (pathname || '').match(/^(.*\/collections\/[^/]+)(?:\/|$)/);
  return match ? match[1] : null;
}

/**
 * The legacy tag segment of a collection URL, or null when there isn't one.
 * A trailing slash (what the old Clear All produced) is not a tag segment.
 */
export function legacyPathSegment(pathname: string): string | null {
  const path = (pathname || '').replace(/\/+$/, '');
  const base = collectionBasePath(path);
  if (!base || path.length <= base.length) return null;
  const rest = path.slice(base.length + 1);
  if (!rest || rest.indexOf('/') !== -1) return null;
  return rest;
}

/** Groups whose mechanism changed, so an unoffered legacy value can't be kept as a tag. */
const SUPERSEDED_GROUPS: Record<string, boolean> = { price: true, size: true };

/**
 * Map one legacy tag string onto a group and one or more values in the new configuration.
 * More than one only for a legacy price bucket the offered ones cover jointly. Null only
 * for a `price=` tag with no exact cover; everything else resolves, falling through to
 * LEGACY_TAG_GROUP_KEY (via `resolveTag`) when no configured group claims it.
 */
export function groupValueForLegacyTag(
  tag: string
): { groupKey: string; values: string[] } | null {
  const raw = (tag || '').trim();
  if (!raw) return null;

  const eq = raw.indexOf('=');
  if (eq !== -1) {
    const prefix = raw.slice(0, eq + 1);
    const value = raw.slice(eq + 1).trim();
    if (!value) return null;
    // Price moved from tags to native ranges, so a legacy bucket has to land on offered
    // ones: itself if it still exists, an exact cover if one is listed, nothing otherwise.
    // It must never survive as a tag — see the note at the top of the file.
    if (prefix === 'price=') {
      if (PRICE_RANGES.hasOwnProperty(value)) return { groupKey: 'price', values: [value] };
      if (LEGACY_PRICE_EXPANSIONS.hasOwnProperty(value)) {
        return { groupKey: 'price', values: LEGACY_PRICE_EXPANSIONS[value].slice() };
      }
      return null;
    }
    if (LEGACY_PREFIX_GROUPS.hasOwnProperty(prefix)) {
      return { groupKey: LEGACY_PREFIX_GROUPS[prefix], values: [value] };
    }
  } else if (raw.indexOf('Filter') !== 0) {
    // Sizes were bare tags (`XXL`). Standard sizes resolve here and are filtered out by
    // the caller, because the size group only offers extended sizes.
    const canonical = SIZE_INDEX[raw.toLowerCase()];
    if (canonical) return { groupKey: 'size', values: [canonical] };
  }

  // Every tag group, the `Filter…` colour sentinel, and finally the raw-tag catch-all.
  const resolved = resolveTag(raw);
  return resolved ? { groupKey: resolved.group.key, values: [resolved.value] } : null;
}

/**
 * The sidebar's spelling of `value`, or null when this group offers no such option.
 * Groups absent from the sidebar altogether (minimum, dimensions, capacity, sizing,
 * supplier, and the raw-tag catch-all) have no option list to check against, so their
 * values pass through — the engine still filters on them and a chip still removes them.
 */
function offeredValue(
  groups: LegacyOptionGroup[],
  groupKey: string,
  value: string
): string | null {
  const group = (groups || []).filter((g) => g && g.key === groupKey)[0];
  if (!group) return value;
  const options = group.options || [];
  for (let i = 0; i < options.length; i++) {
    if (options[i] && options[i].value === value) return options[i].value;
  }
  // Vendor casing is inconsistent in the product data (`adidas` vs `Adidas`), so fall
  // back to a case-insensitive match and adopt the sidebar's spelling.
  const lower = value.toLowerCase();
  for (let i = 0; i < options.length; i++) {
    if (options[i] && options[i].value.toLowerCase() === lower) return options[i].value;
  }
  return null;
}

function addValue(selection: FilterSelection, groupKey: string, value: string): void {
  if (!selection[groupKey]) selection[groupKey] = [];
  if (selection[groupKey].indexOf(value) === -1) selection[groupKey].push(value);
}

/** Exact translation from `collection.current_tags`. */
export function selectionFromLegacyTags(
  tags: string[],
  groups: LegacyOptionGroup[]
): { selection: FilterSelection; matched: string[]; dropped: string[] } {
  const selection: FilterSelection = {};
  const matched: string[] = [];
  const dropped: string[] = [];

  (tags || []).forEach((tag) => {
    const hit = groupValueForLegacyTag(tag);
    if (!hit || !getGroup(hit.groupKey)) {
      dropped.push(tag);
      return;
    }
    // Values the sidebar doesn't offer: for Price and Size the mechanism changed, so they
    // can't survive; for every other group the underlying tag or vendor is still on the
    // products, so keep filtering by it exactly as the legacy URL did.
    const kept: string[] = [];
    hit.values.forEach((value) => {
      const offered = offeredValue(groups, hit.groupKey, value);
      if (offered === null && SUPERSEDED_GROUPS[hit.groupKey]) return;
      kept.push(offered !== null ? offered : value);
    });
    if (kept.length === 0) {
      dropped.push(tag);
      return;
    }
    kept.forEach((value) => addValue(selection, hit.groupKey, value));
    matched.push(tag);
  });

  return { selection: selection, matched: matched, dropped: dropped };
}

/** Every legacy tag spelling that a sidebar option could have been linked as. */
function legacyTagsForValue(group: FilterGroupConfig, value: string): string[] {
  switch (group.type) {
    case 'vendor':
      return ['brand=' + value];
    case 'productType':
      return ['product_type=' + value];
    case 'price':
      return ['price=' + value];
    case 'variantOption':
      // Legacy size tags were bare and inconsistently spelled (`XXL`, `XX-Large`).
      return ((group.valueSynonyms && group.valueSynonyms[value]) || [value]).slice();
    default:
      return [tagForValue(group, value)];
  }
}

/** handleized legacy token -> the group + value(s) it came from. */
function tokenIndex(
  groups: LegacyOptionGroup[]
): Record<string, { groupKey: string; values: string[] }> {
  const index: Record<string, { groupKey: string; values: string[] }> = {};
  const add = (token: string, groupKey: string, values: string[]) => {
    if (token && !index.hasOwnProperty(token)) index[token] = { groupKey: groupKey, values: values };
  };

  (groups || []).forEach((group) => {
    if (!group || !group.key) return;
    const cfg = getGroup(group.key);
    if (!cfg) return;
    (group.options || []).forEach((option) => {
      if (!option || !option.value) return;
      legacyTagsForValue(cfg, option.value).forEach((tag) => {
        add(handleize(tag), group.key, [option.value]);
      });
    });
  });

  // Legacy-only price buckets, so a hand-written /price-under-25 resolves here too.
  Object.keys(LEGACY_PRICE_EXPANSIONS).forEach((label) => {
    const values = LEGACY_PRICE_EXPANSIONS[label].filter(
      (value) => offeredValue(groups, 'price', value) !== null
    );
    if (values.length === LEGACY_PRICE_EXPANSIONS[label].length) {
      add(handleize('price=' + label), 'price', values);
    }
  });

  return index;
}

/**
 * Fallback translation straight off the URL. Only values the sidebar offers can be
 * recovered here; handleization can't be reversed.
 *
 * Note this can't do what `selectionFromLegacyTags` does for unmodelled tags: `electronics`
 * gives no way back to the tag's real casing (`Electronics`), and the API's tag filter
 * wants it exact, so guessing would filter to nothing. Unknown tokens are dropped instead.
 * This only runs when Liquid handed us no `current_tags` at all, which a Shopify-served
 * legacy URL always does.
 */
export function selectionFromLegacyPath(
  segment: string,
  groups: LegacyOptionGroup[]
): { selection: FilterSelection; matched: string[]; dropped: string[] } {
  const selection: FilterSelection = {};
  const matched: string[] = [];
  const dropped: string[] = [];
  const index = tokenIndex(groups);

  safeDecode(segment || '')
    .split('+')
    .forEach((raw) => {
      const token = handleize(raw);
      if (!token) return;
      const hit = index[token];
      if (hit) {
        hit.values.forEach((value) => addValue(selection, hit.groupKey, value));
        matched.push(raw);
      } else {
        dropped.push(raw);
      }
    });

  return { selection: selection, matched: matched, dropped: dropped };
}

/**
 * Translate a legacy collection URL. Returns null when the URL carries no tag segment.
 *
 * A legacy URL that resolves to nothing still returns an adoption with an empty
 * selection: the caller should rewrite to `canonicalPath` either way, so the stale tag
 * segment can't go on fighting the engine's own query-string URLs.
 */
export function readLegacyFilterUrl(opts: {
  pathname: string;
  legacyTags?: string[] | null;
  groups: LegacyOptionGroup[];
}): LegacyAdoption | null {
  const segment = legacyPathSegment(opts.pathname);
  if (!segment) return null;

  const base = collectionBasePath((opts.pathname || '').replace(/\/+$/, '')) || opts.pathname;
  const tags = opts.legacyTags || [];
  const parsed = tags.length
    ? selectionFromLegacyTags(tags, opts.groups)
    : selectionFromLegacyPath(segment, opts.groups);

  return {
    selection: parsed.selection,
    canonicalPath: base,
    matched: parsed.matched,
    dropped: parsed.dropped,
    source: tags.length ? 'tags' : 'path',
  };
}

/** Union two selections, keeping `base`'s ordering and spelling on collisions. */
export function mergeSelections(base: FilterSelection, extra: FilterSelection): FilterSelection {
  const out: FilterSelection = {};
  Object.keys(base || {}).forEach((key) => {
    out[key] = (base[key] || []).slice();
  });
  Object.keys(extra || {}).forEach((key) => {
    (extra[key] || []).forEach((value) => addValue(out, key, value));
  });
  return out;
}
